import { z } from "zod";

import { NoteSchema } from "../note";
import { BrazilAddressSchema } from "./address";
import { cnpjSchema } from "./br-tax-id";
import { BranchSchema } from "./branch";
import {
  clientMetadataFields,
  CLIENT_RECORD_GROUPS,
  ContractSchema,
  FleetEntrySchema,
  PaymentTermsSchema,
} from "./common";
import { ContactSchema } from "./contact";

export const LeadSource = z.enum(["social_media", "referral", "online_search", "sales_consultant"]);

export type LeadSource = z.infer<typeof LeadSource>;

export const CompanyType = z.enum(
  [
    "carrier",
    "transport",
    "rental_agency",
    "insurance",
    "company",
    "association",
    "cooperative",
    "union",
    "app_platform",
    "logistics",
    "other",
  ],
  { message: "clients_validation_company_type_required" },
);

export type CompanyType = z.infer<typeof CompanyType>;

const companyCommonFields = {
  taxId: cnpjSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  notes: z.array(NoteSchema).default([]).meta({ visibleTo: CLIENT_RECORD_GROUPS }),
};

export const CompanyClientFieldsSchema = z
  .object({
    type: z.literal("company").meta({ visibleTo: "*" }),
    name: z
      .string({ message: "clients_validation_company_name_required" })
      .min(1, { message: "clients_validation_company_name_required" })
      .max(200)
      .meta({ visibleTo: "*" }),
    tradeName: z.string().max(200).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    companyType: CompanyType.meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    economicGroup: z.string().max(120).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    address: BrazilAddressSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    mainContact: ContactSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    additionalContacts: z
      .array(ContactSchema)
      .max(2)
      .default([])
      .meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    leadSource: LeadSource.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    salesConsultantId: z.string().max(100).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    fleet: z.array(FleetEntrySchema).default([]).meta({
      visibleTo: CLIENT_RECORD_GROUPS,
    }),
    contract: ContractSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    paymentTerms: PaymentTermsSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    usesExternalCaseIdentifier: z
      .boolean()
      .default(false)
      .meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  })
  .describe("Company client (CNPJ) profile fields.");

export type CompanyClientFields = z.infer<typeof CompanyClientFieldsSchema>;

export const CreateCompanyClientSchema = CompanyClientFieldsSchema.extend({
  taxId: cnpjSchema.optional(),
  notes: z.string().max(4000).optional(),
}).describe("Payload to create a company client.");

export const CompanyClientSchema = CompanyClientFieldsSchema.extend(companyCommonFields)
  .extend({ branches: z.array(BranchSchema).default([]).meta({ visibleTo: "*" }) })
  .extend(clientMetadataFields)
  .describe("Persisted company client document.");
